// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (skipper)
// ==============================================
// /dashboard/papers/requests[?vessel=<vid>] — the crew paper requests I have
// made for a vessel I own or captain (each skipper sees only their own).
// ==============================================

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { apiError } from '../apiError';
import { fmtDay, passageLabel } from '../dates';
import { FormError, S, StatusChip } from './skipperUi';

export function RequestsListPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const access = useQuery({ queryKey: ['papers-access'], queryFn: () => api.papersAccess() });
  const vessels = (access.data?.vessels ?? []).filter((v) => v.role === 'owner' || v.role === 'captain');
  const vid = params.get('vessel') ?? vessels[0]?.id ?? null;
  const list = useQuery({ queryKey: ['skipper-requests', vid], queryFn: () => api.skipperRequests(vid as string), enabled: !!vid, retry: false });

  if (access.isLoading) return <div className="p-10 text-center text-base text-slate-400" role="status">Loading…</div>;
  if (!vessels.length) return <div className="mx-auto max-w-xl p-6"><FormError>Only a vessel’s owner or captain can ask crew for papers.</FormError></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-3 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-2">
          <nav aria-label="Breadcrumb" className={S.label}><Link to={`/dashboard/papers?vessel=${vid}`} className="hover:text-white">Ship’s Papers</Link></nav>
          <h1 className="m-0 text-3xl font-bold text-white">Crew papers requests</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {vessels.length > 1 && (
            <select aria-label="Vessel" value={vid ?? ''} onChange={(e) => navigate(`/dashboard/papers/requests?vessel=${e.target.value}`, { replace: true })} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-800 px-3 text-base text-white">
              {vessels.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          )}
          <Link to={`/dashboard/papers/requests/new?vessel=${vid}`} className={S.primary}>Request crew papers</Link>
        </div>
      </div>
      {list.error && <FormError>{apiError(list.error, 'Your requests could not be loaded.').message}</FormError>}
      {list.isLoading && <p className="text-base text-slate-400" role="status">Loading…</p>}
      {list.data && list.data.length === 0 && (
        <div className={`${S.card} p-6 text-base text-slate-300`}>No requests yet. Ask your crew for their passports, visas, insurance and an emergency contact before a passage — each sends them from their phone.</div>
      )}
      {list.data && list.data.length > 0 && (
        <ul className={`${S.card} m-0 list-none divide-y divide-slate-700/70 overflow-hidden p-0`}>
          {list.data.map((r) => (
            <li key={r.id}>
              <Link to={r.status === 'draft' ? `/dashboard/papers/requests/${r.id}/edit?vessel=${vid}` : `/dashboard/papers/requests/${r.id}?vessel=${vid}`}
                className="flex min-h-[64px] flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70">
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-base font-semibold text-white">{passageLabel(r.fromPlace, r.toPlace) ?? 'Passage not named yet'}</span>
                  <span className="text-sm text-slate-400">
                    {r.departOn ? `Sails ${fmtDay(r.departOn)}` : 'No dates yet'}{r.dueOn ? ` · due ${fmtDay(r.dueOn)}` : ''} · {r.counts.done} of {r.counts.recipients} crew done
                  </span>
                </span>
                <StatusChip status={r.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default RequestsListPage;
